import OrderMapPresenter from "./order-map.js";
import OrderSubmitPresenter from "./order-submit.js";

import OrderFormView from "../view/form.js";
import RadioLabelView from "../view/radio-label.js";
import TitleSectionView from "../view/title-section.js";
import PromocodeSectionView from "../view/promocode-section.js";
import OrderSectionView from "../view/order-section.js";

import OrderFormModel from "../model/order-form";

import Animation from "../../animation/animation.js";
import ScrollToElement from "../../scroll-to-element/scroll-to-element.js";

import {
  AnimationClass
} from "../../animation/const.js";

import {
  removeElement,
  render,
  RenderPosition,
  replace
} from "../../render/render.js";

import {
  defaultOrderFormModelData,
  FormProperty,
  DeliveryValue,
  ActionType,
  CONNECTOR_URL,
  RequestAction,
  SubmitRule
} from "../const.js";
export default class OrderForm {
  constructor(formElement, titleSectionElement, promocodeSectionElement, orderSectionElement) {
    this._formElement = formElement;
    this._titleSectionElement = titleSectionElement;
    this._promocodeSectionElement = promocodeSectionElement;
    this._orderSectionElement = orderSectionElement;

    this._animation = new Animation();
    this._scrollToElement = new ScrollToElement();

    this._orderMap = null;
    this._orderSubmitPresenter = null;

    this._formView = null;
    this._titleSectionView = null;
    this._promocodeSectionView = null;
    this._orderSectionView = null;

    this._radioLabelsViews = [];
    this._model = null;

    this._handleRadioInput = this._handleRadioInput.bind(this);
    this._handleChangeData = this._handleChangeData.bind(this);
    this._handleOrderSubmitLoadStart = this._handleOrderSubmitLoadStart.bind(this);
    this._handleOrderSubmitLoadSuccess = this._handleOrderSubmitLoadSuccess.bind(this);
    this._handleOrderSubmitLoadError = this._handleOrderSubmitLoadError.bind(this);
    this._handleOrderSubmitLoadEnd = this._handleOrderSubmitLoadEnd.bind(this);
  }

  init() {
    this._formView = new OrderFormView(this._formElement);
    this._model = new OrderFormModel(Object.assign(
        {},
        defaultOrderFormModelData
    ));
    this._model.addObserver(this._handleChangeData);

    if (this._formView.getFormPropertyValue(FormProperty.DELIVERY) === DeliveryValue.DELIVERY) {
      this._model.changeData(Object.assign(
          {},
          this._model.getData(),
          {
            isDeliveryRendered: true,
            isDeliveryShown: true,
          }
      ), ActionType.BUILD_DELIVERY_ITEMS);
    }

    this._initRadioLabelsViews();
    this._initOrderSubmit();
  }

  _showSuccessOrderNotification(orderNum, orderCreatedon) {
    this._titleSectionView = new TitleSectionView(this._titleSectionElement, orderNum, orderCreatedon);
    this._orderSectionView = new OrderSectionView(this._orderSectionElement);
    this._promocodeSectionView = new PromocodeSectionView(this._promocodeSectionElement);

    this._scrollToElement.scroll({
      element: this._titleSectionView.getElement()
    });

    this._animation.slideUp(
        this._promocodeSectionView.getElement(),
        null,
        () => {
          removeElement(this._promocodeSectionView.getElement());
          this._promocodeSectionView = null;
        }
    );

    this._animation.slideUp(
        this._orderSectionView.getElement(),
        null,
        () => {
          removeElement(this._orderSectionView.getElement());
          this._orderSectionView = null;
        }
    );

    this._animation.slideUp(
        this._titleSectionView.getProductsWrapperElement(),
        null,
        () => {
          removeElement(this._titleSectionView.getProductsWrapperElement());
        }
    );

    replace(this._titleSectionView.getSuccessNotificationElement(), this._titleSectionView.getCartHeadingWrapperElement());
  }

  _initOrderSubmit() {
    const elementRules = this._prepareOrderSubmitElementRules();

    this._orderSubmitPresenter = new OrderSubmitPresenter({
      formElement: this._formView.getElement(),
      elementRules,
      callbacks: {
        loadStart: this._handleOrderSubmitLoadStart,
        loadSuccess: this._handleOrderSubmitLoadSuccess,
        loadError: this._handleOrderSubmitLoadError,
        loadEnd: this._handleOrderSubmitLoadEnd
      },
      requestUrl: CONNECTOR_URL,
      action: RequestAction.SUBMIT_ORDER,
      submitStyle: `color_white`
    });
    this._orderSubmitPresenter.init(elementRules);
  }

  _updateOrderSubmit() {
    this._orderSubmitPresenter.clearNotifications();
    this._orderSubmitPresenter.setRules(this._prepareOrderSubmitElementRules());
  }

  _prepareOrderSubmitElementRules() {
    return (this._model.getData().isDeliveryShown) ?
      Object.values(SubmitRule) :
      [
        SubmitRule.PHONE,
        SubmitRule.EMAIL
      ];
  }

  _buildDeliveryItems() {
    render(this._formView.getPaymentItemElement(), this._formView.getAddressItemElement(), RenderPosition.BEFOREBEGIN);
    render(this._formView.getPaymentItemElement(), this._formView.getMapItemElement(), RenderPosition.BEFOREBEGIN);

    this._animation.fadeIn(
        this._formView.getAddressItemElement(),
        null,
        () => {
          this._updateRadioLabelsViews();
        }
    );

    this._animation.fadeIn(
        this._formView.getMapItemElement(),
        null,
        () => {
          this._initMap();
        },
        AnimationClass.SHOW_FLEX
    );
  }

  _hideDeliveryItems() {
    this._animation.fadeOut(
        this._formView.getAddressItemElement()
    );

    this._animation.fadeOut(
        this._formView.getMapItemElement()
    );

    this._updateOrderSubmit();
  }

  _showDeliveryItems() {
    this._animation.fadeIn(
        this._formView.getAddressItemElement()
    );

    this._animation.fadeIn(
        this._formView.getMapItemElement()
    );

    this._updateOrderSubmit();
  }

  _initMap() {
    this._orderMapPresenter = new OrderMapPresenter(
        this._formView.getMapContainerElement(),
        this._model
    );
    this._orderMapPresenter.init();
  }

  _initRadioLabelView(element) {
    const view = new RadioLabelView(element);
    view.setCheckedStyles();
    view.setInputHandler(this._handleRadioInput);
    this._radioLabelsViews.push(view);
  }

  _initRadioLabelsViews() {
    const radioLabels = this._formView.getRadioLabelsElements();

    Array.from(radioLabels)
      .forEach((element) => {
        this._initRadioLabelView(element);
      });
  }

  _updateRadioLabelsViews() {
    const radioLabels = this._formView.getRadioLabelsElements();

    Array.from(radioLabels)
      .forEach((element) => {
        const isExists = this._radioLabelsViews.some((view) => element === view.getElement());

        if (!isExists) {
          this._initRadioLabelView(element);
        }
      });
  }

  _formSubmitSuccess() {
    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          isSubmit: true
        }
    ), ActionType.FORM_SUBMIT_SUCCESS);
  }

  _processFormSubmitting() {
    this._formView.setSubmitting();
  }

  _processFormSubmitSuccess(data) {
    // Send the Yandex Metrika goal.
    if (window.ym) {
      window.ym(29276100, `reachGoal`, `cartorder`);
    }

    this._formView.setSuccess();
    this._showSuccessOrderNotification(data.order_num, data.order_createdon);
  }

  _handleRadioInput(evt) {
    this._radioLabelsViews.forEach((view) => view.setCheckedStyles());

    if (evt.target.name !== FormProperty.DELIVERY) {
      return;
    }

    if (!this._model.getData().isDeliveryRendered) {
      this._model.changeData(Object.assign(
          {},
          this._model.getData(),
          {
            isDeliveryRendered: true,
            isDeliveryShown: false,
          }
      ), ActionType.BUILD_DELIVERY_ITEMS);
      return;
    }

    const value = evt.target.value;

    const action = (value === DeliveryValue.SELF_PICKUP) ?
      ActionType.HIDE_DELIVERY_ITEMS :
      ActionType.SHOW_DELIVERY_ITEMS;
    const isDeliveryShown = (value === DeliveryValue.SELF_PICKUP) ?
      false :
      true;

    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          isDeliveryShown
        }
    ), action);
  }

  _handleOrderSubmitLoadStart() {
    if (this._model.getData().isSubmitting || this._model.getData().isSubmit) {
      return;
    }

    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          isSubmitting: true
        }
    ), ActionType.FORM_SUBMIT_START);
  }

  _handleOrderSubmitLoadSuccess(data) {
    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          isSubmit: true
        }
    ), ActionType.FORM_SUBMIT_SUCCESS, {
      data
    });
  }

  _handleOrderSubmitLoadError() {
    this._model.changeData(Object.assign(
        {},
        this._model.getData(),
        {
          isSubmitting: false
        }
    ), ActionType.FORM_SUBMIT_ERROR);
  }

  _handleOrderSubmitLoadEnd() {}

  _handleChangeData(action, payload) {
    switch (action) {
      case ActionType.BUILD_DELIVERY_ITEMS:
        this._buildDeliveryItems();
        break;
      case ActionType.SHOW_DELIVERY_ITEMS:
        this._showDeliveryItems();
        break;
      case ActionType.HIDE_DELIVERY_ITEMS:
        this._hideDeliveryItems();
        break;
      case ActionType.SET_PLACEMARK:
        this._formView.setAddressShippingValue(this._model.getData().addressShipping);
        break;
      case ActionType.FORM_SUBMIT_ERROR:
        this._formView.reset();
        break;
      case ActionType.FORM_SUBMIT_SUCCESS:
        this._processFormSubmitSuccess(payload.data.data);
        break;
      case ActionType.FORM_SUBMIT_START:
        this._processFormSubmitting();
        break;
    }
  }
}
